import { isPanelOwned, type CanvasState, type Frame } from '../store/types';
import { openThreads, describeCommentTarget } from '../store/comments';
import { lottieElementsOf } from '../lottie/scan';
import { serializeTimeline } from './serializeTimeline';

const HTML_PREVIEW = 160;

export function serializeForAgent(s: CanvasState): string {
  const frames = s.doc.frames.filter((f) => !isPanelOwned(f));
  const byMainCounts = countInstancesByMain(frames);
  const lines = frames.length === 0 ? ['(empty canvas)'] : frames.map((f) => describeFrame(f, byMainCounts.get(f.id) ?? 0));
  const tours = s.doc.tours ?? [];
  if (tours.length) lines.push(`tours: ${tours.map((t) => `${t.id}="${t.title}"`).join(', ')}`);
  const themes = s.doc.themes ?? [];
  if (themes.length) lines.unshift(`themes: ${themes.map((t) => `${t.id}="${t.name}"`).join(', ')}`);
  if (s.doc.activeThemeId && themes.length) {
    const active = themes.find((t) => t.id === s.doc.activeThemeId);
    lines.unshift(`active theme: ${s.doc.activeThemeId} "${active?.name ?? s.doc.activeThemeId}"`);
  }
  const open = openThreads(s.doc.comments ?? []);
  if (open.length) {
    const CAP = 12;
    lines.push(`open comments (${open.length}):`);
    for (const c of open.slice(0, CAP)) {
      const last = c.messages
        .slice(-3)
        .map((m) => `${m.author}: ${truncate(m.text, 120)}`)
        .join(' | ');
      lines.push(`  ${c.id} @${describeCommentTarget(c.target)}: ${last}`);
    }
    if (open.length > CAP) lines.push(`  …+${open.length - CAP} more`);
  }
  return lines.join('\n');
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}

/** theme + script flags appended to design-frame descriptions. */
function styleBits(f: Frame): string {
  const theme = f.themeId ? ` theme=${f.themeId}` : '';
  const script = (f.kind === 'free' || f.kind === 'main') && f.script ? ' script=yes' : '';
  return theme + script;
}

function motionBits(f: Frame): string {
  const tl = 'motion' in f ? (f as { motion?: import('../store/types').MotionTimeline }).motion : undefined;
  if (!tl || tl.tracks.length === 0) return '';
  // Full keyframe detail, indented so it nests under the frame line.
  return '\n  ' + serializeTimeline(tl).split('\n').join('\n  ');
}

function lottieBits(f: Frame): string {
  if (!('html' in f)) return '';
  const els = lottieElementsOf((f as { html: string }).html);
  if (els.length === 0) return '';
  const parts = els.map((e) => {
    const slotNames = Object.keys(e.slots);
    return `${e.cfNodeId}${e.loop ? '' : ' once'}${slotNames.length ? ` slots(${slotNames.join(',')})` : ''}`;
  });
  return ` lottie=[${parts.join('; ')}]`;
}

function describeFrame(f: Frame, instanceCount: number): string {
  const h = f.h ?? 'auto';
  const common = `${f.id} "${f.title}" x=${f.x} y=${f.y} w=${f.w} h=${h} effect=${f.effect.kind}`;
  if (f.kind === 'free') {
    const html = preview(f.html);
    return `- free ${common}${styleBits(f)}${motionBits(f)}${lottieBits(f)} html=${JSON.stringify(html)}`;
  }
  if (f.kind === 'main') {
    const html = preview(f.html);
    const slotNames = f.slots.map((s) => s.name).join(',');
    const lock = f.locked ? ' locked' : '';
    return `- main ${common}${lock}${styleBits(f)}${motionBits(f)}${lottieBits(f)} instances=${instanceCount} slots=[${slotNames}] html=${JSON.stringify(html)}`;
  }
  if (f.kind === 'instance') {
    const vals = Object.entries(f.slotValues)
      .map(([k, v]) => `${k}=${JSON.stringify(preview(v))}`)
      .join(' ');
    return `- instance ${common}${styleBits(f)} mainId=${f.mainId} ${vals}`;
  }
  // TSX frames
  if (f.kind === 'tsxMain') {
    const props = f.propsSchema.map((p) => `${p.name}:${p.type}`).join(',');
    return `- tsxMain id=${f.id} title=${JSON.stringify(f.title)}${motionBits(f)} instances=${instanceCount} props=${props || 'none'}`;
  }
  if (f.kind === 'tsxInstance') {
    const overrides = Object.entries(f.propValues).map(([k, v]) => `${k}=${v}`).join(' ');
    return `- tsxInstance id=${f.id} →${f.mainId}${overrides ? ' ' + overrides : ''}`;
  }
  if (f.kind === 'tsxFree') {
    const first = (f.tsx.split('\n').find((l) => l.trim()) ?? '').slice(0, 60);
    return `- tsxFree id=${f.id} title=${JSON.stringify(f.title)}${motionBits(f)} ${first}`;
  }
  // Tool frames (chat / code / fx / note / styleguide)
  if (f.kind === 'styleguide') {
    return `- styleguide ${common} shows-theme=${f.themeId ?? 'active'}`;
  }
  if (f.kind === 'chat') {
    return `- chat ${common} anchor=${f.anchorId ?? 'global'} msgs=${f.messages.length} pinned=${f.pinned}`;
  }
  if (f.kind === 'note') {
    const tgt = f.target ? `${f.target.kind}${f.target.kind !== 'pin' ? ':' + f.target.frameId : ''}` : 'none';
    return `- note ${common} target=${tgt} tour=${f.tourId ?? '-'} step=${f.step ?? '-'} html=${JSON.stringify(preview(f.html))}`;
  }
  return `- ${f.kind} ${common} anchor=${f.anchorId ?? 'global'}`;
}

function preview(s: string): string {
  return s.length > HTML_PREVIEW ? s.slice(0, HTML_PREVIEW) + '…' : s;
}

function countInstancesByMain(frames: Frame[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const f of frames) {
    if (f.kind === 'instance' || f.kind === 'tsxInstance') {
      counts.set(f.mainId, (counts.get(f.mainId) ?? 0) + 1);
    }
  }
  return counts;
}
